/** Campaign status options */
export const CLAIM_CAMPAIGN_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'EXPIRED'] as const;
export type ClaimCampaignStatus = (typeof CLAIM_CAMPAIGN_STATUSES)[number];

/** Campaign type - NORMAL for code/API-validated campaigns, MATCH for eligibility-matched campaigns */
export const CLAIM_CAMPAIGN_TYPES = ['NORMAL', 'MATCH'] as const;
export type ClaimCampaignType = (typeof CLAIM_CAMPAIGN_TYPES)[number];

/** Validator types for claim campaigns */
export const CLAIM_VALIDATOR_TYPES = ['PREGEN_CODE', 'SHARED_CODE', 'EXTERNAL_API', 'DIRECT'] as const;
export type ClaimValidatorType = (typeof CLAIM_VALIDATOR_TYPES)[number];

/** Match check types */
export const CLAIM_MATCH_CHECK_TYPES = ['BQ_QUERY'] as const;
export type ClaimMatchCheckType = (typeof CLAIM_MATCH_CHECK_TYPES)[number];

/** Evidence field type options for the admin UI */
export const EVIDENCE_FIELD_TYPES = ['text', 'dropdown', 'file'] as const;
export type EvidenceFieldType = (typeof EVIDENCE_FIELD_TYPES)[number];

/** A single evidence field definition for campaigns with requireApproval */
export interface EvidenceField {
  name: string;
  label: string;
  description?: string;
  type?: EvidenceFieldType;
  options?: Array<string>;
  multiple?: boolean;
}

/** Individual check definition within a multi-check config */
export interface MatchCheckDefinition {
  key: string;
  eligibilityTemplate: string;
}

/** Config for BQ_QUERY match check */
export interface BqQueryMatchCheckConfig {
  type: 'BQ_QUERY';
  /** SQL query - must SELECT customer_bk or customer_id, and filter with: WHERE customer_bk IN UNNEST(@customerIds) */
  bqQuery: string;
  checks: Array<MatchCheckDefinition>;
}

export type MatchCheckConfig = BqQueryMatchCheckConfig;

/** Action types that can be configured for a campaign */
export const CLAIM_ACTION_TYPES = ['TIER_UPGRADE', 'BONUS_POINTS', 'STATUS_CREDITS', 'DYNAMIC_TIER_UPGRADE'] as const;
export type ClaimActionType = (typeof CLAIM_ACTION_TYPES)[number];

/** Structured deny reasons used when rejecting a CS approval request. Kept in sync with the BE. */
export const DENY_REASONS = [
  'INSUFFICIENT_EVIDENCE',
  'INVALID_EVIDENCE',
  'NOT_ELIGIBLE',
  'EXPIRED_DOCUMENT',
  'DUPLICATE_SUBMISSION',
  'OTHER',
] as const;
export type DenyReason = (typeof DENY_REASONS)[number];

/** The "Other" deny reason requires free-text notes to explain. */
export const isOtherReason = (reason: DenyReason | '' | null | undefined): boolean => reason === 'OTHER';

/** Allowed range for the `maxAppeals` field on a claim campaign. */
export const MIN_APPEALS = 0;
export const MAX_APPEALS = 10;

export const DENY_REASON_LABELS: Record<DenyReason, string> = {
  INSUFFICIENT_EVIDENCE: 'Insufficient evidence provided',
  INVALID_EVIDENCE: 'Evidence could not be verified',
  NOT_ELIGIBLE: 'Not eligible for this campaign',
  EXPIRED_DOCUMENT: 'Evidence document is expired',
  DUPLICATE_SUBMISSION: 'Duplicate submission',
  OTHER: 'Other (see notes)',
};

/** Safely resolves a deny-reason label. Falls back to the raw value for forward-compatibility. */
export function getDenyReasonLabel(reason: string | null | undefined): string {
  if (!reason) {
    return '';
  }
  return DENY_REASON_LABELS[reason as DenyReason] ?? reason;
}

/** Code status options */
export const CLAIM_CODE_STATUSES = ['AVAILABLE', 'CLAIMED', 'EXPIRED', 'REVOKED'] as const;
export type ClaimCodeStatus = (typeof CLAIM_CODE_STATUSES)[number];

/** Claim record status options */
export const CLAIM_RECORD_STATUSES = [
  'PENDING',
  'VALIDATED',
  'COMPLETED',
  'PARTIAL_FAILED',
  'FAILED',
  'REJECTED',
] as const;
export type ClaimRecordStatus = (typeof CLAIM_RECORD_STATUSES)[number];

/** Claim action execution status */
export const CLAIM_ACTION_STATUSES = ['PENDING', 'PROCESSING', 'COMPLETED', 'FAILED', 'SKIPPED'] as const;
export type ClaimActionStatus = (typeof CLAIM_ACTION_STATUSES)[number];

/** Target tier options for TIER_UPGRADE actions */
export const CLAIM_TARGET_TIERS = ['silver', 'gold', 'platinum'] as const;
export type ClaimTargetTier = (typeof CLAIM_TARGET_TIERS)[number];

/** HTTP methods for EXTERNAL_API validator */
export const EXTERNAL_API_METHODS = ['GET', 'POST'] as const;
export type ExternalApiMethod = (typeof EXTERNAL_API_METHODS)[number];

// ---------------------------------------------------------------------------
// Validator Config Types
// ---------------------------------------------------------------------------

/** Config for PREGEN_CODE validator (no extra fields needed) */
export interface PregenCodeValidatorConfig {
  type: 'PREGEN_CODE';
}

/** Config for SHARED_CODE validator (no extra fields needed) */
export interface SharedCodeValidatorConfig {
  type: 'SHARED_CODE';
}

/** Config for EXTERNAL_API validator */
export interface ExternalApiValidatorConfig {
  type: 'EXTERNAL_API';
  endpoint: string;
  method: ExternalApiMethod;
  headers?: Record<string, string>;
  requestTemplate?: Record<string, unknown>;
  successPath: string;
  errorPath?: string;
  timeout: number;
}

/** Config for DIRECT validator (no extra fields needed) */
export interface DirectValidatorConfig {
  type: 'DIRECT';
}

export type ValidatorConfig =
  | PregenCodeValidatorConfig
  | SharedCodeValidatorConfig
  | ExternalApiValidatorConfig
  | DirectValidatorConfig;

// ---------------------------------------------------------------------------
// Action Config Types
// ---------------------------------------------------------------------------

/** Config for TIER_UPGRADE action */
export interface TierUpgradeActionConfig {
  type: 'TIER_UPGRADE';
  targetTier: ClaimTargetTier;
  reasonCode: string;
  remarks: string;
}

/** Config for BONUS_POINTS action */
export interface BonusPointsActionConfig {
  type: 'BONUS_POINTS';
  bonusPoints: number;
  activityDescription: string;
}

/** Config for STATUS_CREDITS action */
export interface StatusCreditsActionConfig {
  type: 'STATUS_CREDITS';
  statusCredits: number;
  bonusStatusCredits?: number;
  activityDescription: string;
}

/** A single entry in the dynamic tier upgrade reward mapping */
export interface RewardMappingEntry {
  match: Record<string, string>;
  targetTier: ClaimTargetTier;
  remarks: string;
}

/** Config for DYNAMIC_TIER_UPGRADE action */
export interface DynamicTierUpgradeActionConfig {
  type: 'DYNAMIC_TIER_UPGRADE';
  rewardMapping: Array<RewardMappingEntry>;
  reasonCode?: string;
}

export type ActionConfig =
  | TierUpgradeActionConfig
  | BonusPointsActionConfig
  | StatusCreditsActionConfig
  | DynamicTierUpgradeActionConfig;

// ---------------------------------------------------------------------------
// Entity Types
// ---------------------------------------------------------------------------

/** Campaign entity returned by the admin API */
export interface ClaimCampaign {
  id: string;
  name: string;
  description?: string | null;
  status: ClaimCampaignStatus;
  type: ClaimCampaignType;
  validatorType: ClaimValidatorType;
  validatorConfig: Record<string, unknown>;
  actions: Record<string, unknown>[];
  maxClaimsPerUser: number;
  maxAppeals?: number;
  enrollmentValidityDays?: number;
  matchCheckType?: ClaimMatchCheckType | null;
  matchCheckConfig?: MatchCheckConfig | null;
  requireApproval: boolean;
  evidenceFields?: EvidenceField[] | null;
  startDate?: string | null;
  endDate?: string | null;
  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Total claim records for this campaign (all statuses). */
  totalClaims?: number;
}

/** Code entity returned by the admin API */
export interface ClaimCode {
  id: string;
  code: string;
  status: ClaimCodeStatus;
  claimedBy?: string | null;
  claimedAt?: string | null;
  expiresAt?: string | null;
  maxRedemptions?: number | null;
  currentRedemptions: number;
  createdAt: string;
}

/** Individual action result within a claim record */
export interface ClaimActionRecord {
  id: string;
  actionType: ClaimActionType;
  status: ClaimActionStatus;
  result?: Record<string, unknown> | null;
  errorMessage?: string | null;
  executedAt?: string | null;
}

/** Claim record entity returned by the admin API */
export interface ClaimRecord {
  id: string;
  customerId: string;
  accountId: string;
  status: ClaimRecordStatus;
  validationInput?: Record<string, unknown> | null;
  validationResult?: Record<string, unknown> | null;
  errorMessage?: string | null;
  claimedAt?: string | null;
  createdAt: string;
  actions: ClaimActionRecord[];
}

// ---------------------------------------------------------------------------
// Request Params
// ---------------------------------------------------------------------------

/** Params for listing campaigns */
export interface ListClaimCampaignsParams {
  status?: ClaimCampaignStatus;
  limit?: number;
  offset?: number;
}

/** Body for creating a campaign */
export interface CreateClaimCampaignBody {
  name: string;
  description?: string;
  status?: ClaimCampaignStatus;
  type?: ClaimCampaignType;
  validatorType: ClaimValidatorType;
  validatorConfig: Record<string, unknown>;
  requireApproval?: boolean;
  evidenceFields?: EvidenceField[];
  matchCheckType?: ClaimMatchCheckType;
  matchCheckConfig?: Record<string, unknown>;
  actions: ActionConfig[];
  maxClaimsPerUser?: number;
  maxAppeals?: number;
  enrollmentValidityDays?: number;
  startDate?: string;
  endDate?: string;
}

/** Body for updating a campaign */
export interface UpdateClaimCampaignBody {
  name?: string;
  description?: string;
  status?: ClaimCampaignStatus;
  validatorConfig?: Record<string, unknown>;
  actions?: ActionConfig[];
  requireApproval?: boolean;
  maxClaimsPerUser?: number;
  maxAppeals?: number;
  enrollmentValidityDays?: number;
  matchCheckType?: ClaimMatchCheckType;
  matchCheckConfig?: Record<string, unknown>;
  evidenceFields?: EvidenceField[];
  startDate?: string;
  endDate?: string;
}

/** Body for uploading codes */
export interface UploadClaimCodesBody {
  codes: string[];
  expiresAt?: string;
  maxRedemptions?: number;
}

/** Params for listing codes */
export interface ListClaimCodesParams {
  status?: ClaimCodeStatus;
  limit?: number;
  offset?: number;
}

/** Params for listing claim records */
export interface ListClaimRecordsParams {
  status?: ClaimRecordStatus;
  limit?: number;
  offset?: number;
}

// ---------------------------------------------------------------------------
// Response Types
// ---------------------------------------------------------------------------

/** Response from listing campaigns */
export interface ListClaimCampaignsResponse {
  status: number;
  message: string;
  result: {
    campaigns: ClaimCampaign[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  };
}

/** Response from creating a campaign */
export interface CreateClaimCampaignResponse {
  status: number;
  message: string;
  result: {
    campaign: ClaimCampaign;
  };
}

/** Response from getting a single campaign */
export interface GetClaimCampaignResponse {
  status: number;
  message: string;
  result: {
    campaign: ClaimCampaign;
  };
}

/** Response from updating a campaign */
export interface UpdateClaimCampaignResponse {
  status: number;
  message: string;
  result: {
    campaign: ClaimCampaign;
  };
}

/** Response from deleting a campaign */
export interface DeleteClaimCampaignResponse {
  status: number;
  message: string;
}

/** Response from uploading codes */
export interface UploadClaimCodesResponse {
  status: number;
  message: string;
  result: {
    requestedCount: number;
    createdCount: number;
    duplicatesSkipped: number;
  };
}

/** Response from listing codes */
export interface ListClaimCodesResponse {
  status: number;
  message: string;
  result: {
    codes: ClaimCode[];
    summary: Record<ClaimCodeStatus, number>;
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  };
}

/** Response from listing claim records */
export interface ListClaimRecordsResponse {
  status: number;
  message: string;
  result: {
    claims: ClaimRecord[];
    pagination: {
      total: number;
      limit: number;
      offset: number;
    };
  };
}

// ---------------------------------------------------------------------------
// Internal Claim (admin submit on behalf of customer)
// ---------------------------------------------------------------------------

/** Body for submitting a claim on behalf of a customer */
export interface InternalSubmitClaimBody {
  customerId: string;
  code?: string;
  additionalData?: Record<string, unknown>;
}

/** Individual action result in the internal claim response */
export interface InternalClaimActionResult {
  type: ClaimActionType;
  status: string;
  result?: Record<string, unknown> | null;
  errorCode?: string | null;
  errorMessage?: string | null;
}

/** Response from submitting an internal claim */
export interface InternalSubmitClaimResponse {
  status: number;
  message: string;
  result: {
    claimRecordId: string;
    claimed: boolean;
    actions: InternalClaimActionResult[];
  };
}

// ---------------------------------------------------------------------------
// Code Management (admin update / delete)
// ---------------------------------------------------------------------------

/** Body for updating a claim code */
export interface UpdateClaimCodeBody {
  code?: string;
  expiresAt?: string | null;
  maxRedemptions?: number;
}

/** Response from updating a claim code */
export interface UpdateClaimCodeResponse {
  status: number;
  message: string;
  result: {
    code: ClaimCode;
  };
}

/** Response from deleting a claim code */
export interface DeleteClaimCodeResponse {
  status: number;
  message: string;
}

/** Response from test-match-check dry-run */
export interface TestMatchCheckResponse {
  status: number;
  message: string;
  result: {
    rowCount: number;
    rows: Record<string, unknown>[];
  };
}
